import { useTranslation } from '../../../i18n';
import { getObjectColors } from '../../../utils/objectColors';
import { buildAxis, pctOf, DEFAULT_RANGE } from '../../../utils/timeAxis';
import { minToHHMM, timeToMin } from '../../../utils/dashboardData';
import type { PlanningTask } from '../../../types/dashboard';

interface Props {
  tasks: PlanningTask[];
  workStart?: number;
  workEnd?: number;
  nowMinutes: number;
  compact?: boolean;
}

export function DayTimelineStrip({
  tasks,
  workStart = DEFAULT_RANGE.startHour,
  workEnd = DEFAULT_RANGE.endHour,
  nowMinutes,
  compact = false,
}: Props) {
  const { t } = useTranslation('dashboard');

  const earliest = tasks.reduce(
    (acc, ts) => Math.min(acc, timeToMin(ts.startTime)),
    workStart * 60,
  );
  const latest = tasks.reduce(
    (acc, ts) => Math.max(acc, timeToMin(ts.endTime)),
    workEnd * 60,
  );
  const startHour = Math.max(0, Math.floor(Math.min(workStart * 60, earliest) / 60));
  const endHour = Math.min(24, Math.ceil(Math.max(workEnd * 60, latest) / 60));
  const axis = buildAxis(startHour, endHour);

  const showNow = nowMinutes >= axis.startMin && nowMinutes <= axis.endMin;
  const nowPct = pctOf(nowMinutes, axis.startMin, axis.total);

  const barHeight = compact ? 'h-9' : 'h-9 sm:h-12';

  if (tasks.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-surface dark:bg-slate-900 dark:border-slate-700 p-5 text-center text-sm text-ink-muted dark:text-slate-400">
        {t('timeline.empty')}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border bg-surface dark:bg-slate-900 dark:border-slate-700 p-4 sm:p-5">
      {!compact && (
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono-num text-[10px] uppercase tracking-[0.18em] text-ink-faint">
            {t('timeline.title')}
          </div>
          <div className="font-mono-num text-[11px] text-ink-muted dark:text-slate-400">
            {t('timeline.range', {
              start: minToHHMM(axis.startMin),
              end: minToHHMM(axis.endMin),
            })}
          </div>
        </div>
      )}

      <div className={`relative ${barHeight} rounded-lg bg-surface-sunken dark:bg-slate-800 overflow-hidden`}>
        {tasks.map((task) => {
          const start = timeToMin(task.startTime);
          const end = timeToMin(task.endTime);
          if (end <= start) return null;
          const left = pctOf(start, axis.startMin, axis.total);
          const width = pctOf(end, axis.startMin, axis.total) - left;
          const done = end <= nowMinutes;
          const active = start < nowMinutes && end > nowMinutes;
          const colors = getObjectColors(task.objectId ?? task.objectName ?? task.taskName ?? '');
          return (
            <div
              key={task.planningEntryTaskId ?? `${start}-${end}`}
              className="absolute top-0 bottom-0 rounded-md overflow-hidden ring-1 ring-black/5"
              style={{
                left: `${left}%`,
                width: `${width}%`,
                background: colors.color,
                opacity: done ? 0.4 : 1,
              }}
              title={`${task.objectName ?? ''} · ${task.startTime?.substring(0, 5)} – ${task.endTime?.substring(0, 5)}`}
            >
              {!compact && width > 5 && (
                <div className="px-2 py-1 text-[10px] font-medium text-white truncate">
                  {task.objectShortName ?? task.objectName}
                </div>
              )}
              {active && (
                <div className="absolute inset-x-0 bottom-0 h-0.5 bg-white/80" aria-hidden />
              )}
            </div>
          );
        })}

        {showNow && (
          <div
            className="absolute top-0 bottom-0 w-px bg-ink dark:bg-slate-100 shadow-[0_0_0_1px_white]"
            style={{ left: `${nowPct}%` }}
            aria-label={t('timeline.nowMarker', { time: minToHHMM(nowMinutes) })}
          >
            <div className="absolute -top-1 -left-1 w-2 h-2 rounded-full bg-ink dark:bg-slate-100" />
          </div>
        )}
      </div>

      {!compact && (
        <div className="mt-2 flex justify-between font-mono-num text-[10px] text-ink-faint dark:text-slate-500">
          {axis.hours
            .filter((_, i, arr) => i === 0 || i === arr.length - 1 || i % 2 === 0)
            .map((h) => (
              <span key={h}>{String(h).padStart(2, '0')}:00</span>
            ))}
        </div>
      )}
    </div>
  );
}
